import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Card,
  CardContent,
  Container,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Description as DocumentIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Claim, Config } from '../types';
import { api } from '../services/api';

const ClaimDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [claim, setClaim] = useState<Claim | null>(null);
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (id) {
      fetchClaimAndConfig();
    }
  }, [id]);

  const fetchClaimAndConfig = async () => {
    try {
      setError('');
      const [claimData, configData] = await Promise.all([
        api.getClaim(id!),
        api.getConfig()
      ]);
      setClaim(claimData);
      setConfig(configData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load claim details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: Claim['status']) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'under_review': return 'warning';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: Claim['priority']) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const buildThirdPartyUrl = (): string => {
    if (!config || !claim || !config.thirdPartyUrlTemplate || !config.thirdPartyBaseUrl) return '';

    // Create the claimNumber JSON object as specified
    const claimNumberData = {
      value: claim.id,
      searchType: "STRING_STARTS_WITH",
      toggleKey: "ANY"
    };

    // URL encode the JSON string
    const encodedClaimNumber = encodeURIComponent(JSON.stringify(claimNumberData));

    const template = config.thirdPartyUrlTemplate;
    return template
      .replace('{{baseUrl}}', config.thirdPartyBaseUrl)
      .replace('{{claimNumber}}', encodedClaimNumber);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading claim details...</Typography>
      </Container>
    );
  }

  if (error || !claim) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Claim not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/claims')}
          sx={{ mt: 2 }}
        >
          Back to Claims List
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/claims')}
          sx={{ mr: 2 }}
        >
          Back to Claims
        </Button>
        <Typography variant="h3" component="h1">
          Claim Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  Claim #{claim.id}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {claim.claimantName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                <Chip
                  label={claim.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(claim.status)}
                  size="medium"
                />
                <Chip
                  label={`${claim.priority.toUpperCase()} PRIORITY`}
                  color={getPriorityColor(claim.priority)}
                  variant="outlined"
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Policy Number
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {claim.policyNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Claim Type
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {claim.claimType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date of Loss
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(new Date(claim.dateOfLoss), 'MMMM dd, yyyy')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date Submitted
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {format(new Date(claim.dateSubmitted), 'MMMM dd, yyyy')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Claim Amount
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(claim.amount)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {claim.assignedTo}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1">
                {claim.description}
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  disabled={claim.status === 'approved'}
                  fullWidth
                >
                  Approve Claim
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  disabled={claim.status === 'rejected'}
                  fullWidth
                >
                  Reject Claim
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/*<Card>*/}
          {/*  <CardContent>*/}
          {/*    <Typography variant="h6" gutterBottom>*/}
          {/*      Box.com Features Demo*/}
          {/*    </Typography>*/}
          {/*    <Typography variant="body2" color="text.secondary" gutterBottom>*/}
          {/*      This claim showcases:*/}
          {/*    </Typography>*/}
          {/*    <Box component="ul" sx={{ pl: 2, mt: 1 }}>*/}
          {/*      <Typography component="li" variant="body2">*/}
          {/*        Secure document storage*/}
          {/*      </Typography>*/}
          {/*      <Typography component="li" variant="body2">*/}
          {/*        Version control & metadata*/}
          {/*      </Typography>*/}
          {/*      <Typography component="li" variant="body2">*/}
          {/*        Workflow automation*/}
          {/*      </Typography>*/}
          {/*      <Typography component="li" variant="body2">*/}
          {/*        Advanced search capabilities*/}
          {/*      </Typography>*/}
          {/*      <Typography component="li" variant="body2">*/}
          {/*        Real-time collaboration*/}
          {/*      </Typography>*/}
          {/*    </Box>*/}
          {/*  </CardContent>*/}
          {/*</Card>*/}
        </Grid>
      </Grid>

      {/* Full-width Documents Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Documents
          </Typography>
          <Button
            variant="contained"
            endIcon={<LaunchIcon />}
            onClick={() => window.open(buildThirdPartyUrl(), '_blank')}
            sx={{
              backgroundColor: '#5f0397',
              '&:hover': { backgroundColor: '#470271' },
            }}
          >
            View in Panorama
          </Button>
        </Box>
        
        {/* Embedded Panorama Application */}
        <Box sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          height: '800px',
          overflow: 'hidden'
        }}>
          <iframe
            src={buildThirdPartyUrl()}
            width="100%"
            height="100%"
            frameBorder="0"
            title="Panorama Document Management"
            style={{ 
              border: 'none',
              borderRadius: '4px'
            }}
          />
        </Box>
      </Paper>

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        mt: 4,
        p: 2,
        backgroundColor: '#fafafa',
        borderRadius: 2
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Powered by
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#5f0397' }}>
          Micro Strategies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          • Mostly Safe, Completely Reliable™
        </Typography>
      </Box>
    </Container>
  );
};

export default ClaimDetail;